import { supabase } from '../config/supabase';
import { parseGeoPoint } from '../utils/formatters';

/** Vehicles with a GPS point within this window count as "online". */
export const FLEET_ONLINE_MINUTES = 15;

export type FleetMapDisplayStatus =
  | 'available'
  | 'on_trip'
  | 'pending'
  | 'maintenance'
  | 'stale'
  | 'unknown';

export interface FleetVehiclePosition {
  vehicleId: string;
  fleetNumber: string;
  registrationPlate: string | null;
  vehicleStatus: string;
  fleetOperatorId: string | null;
  lat: number | null;
  lng: number | null;
  lastSeenAt: string | null;
  tripId: string | null;
  driverId: string | null;
  displayStatus: FleetMapDisplayStatus;
  online: boolean;
}

export type FetchLatestVehiclePositionsOptions = {
  fleetOperatorId?: string | null;
  onlineMinutes?: number;
};

/**
 * Latest known position per vehicle (from recent location_logs) + ops display status.
 * Owner scope: pass fleetOperatorId (RLS also applies).
 */
export async function fetchLatestVehiclePositions(
  options: FetchLatestVehiclePositionsOptions = {}
): Promise<FleetVehiclePosition[]> {
  const onlineMinutes = options.onlineMinutes ?? FLEET_ONLINE_MINUTES;
  const onlineCutoff = Date.now() - onlineMinutes * 60 * 1000;
  const staleCutoff = Date.now() - 30 * 60 * 1000;
  const logsSince = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const today = new Date().toISOString().slice(0, 10);

  let vehicleQuery = supabase
    .from('vehicles')
    .select('id, fleet_number, registration_plate, status, fleet_operator_id')
    .neq('status', 'retired');

  if (options.fleetOperatorId) {
    vehicleQuery = vehicleQuery.eq('fleet_operator_id', options.fleetOperatorId);
  }

  const { data: vehicles, error: vehicleError } = await vehicleQuery;
  if (vehicleError) throw vehicleError;
  if (!vehicles?.length) return [];

  const vehicleIds = vehicles.map((v) => v.id);

  const [{ data: activeTrips }, { data: pendingTrips }, { data: logs }] = await Promise.all([
    supabase
      .from('trips')
      .select('id, vehicle_id, driver_id, status')
      .in('vehicle_id', vehicleIds)
      .eq('date', today)
      .in('status', ['dispatched', 'in_progress']),
    supabase
      .from('trips')
      .select('id, vehicle_id')
      .in('vehicle_id', vehicleIds)
      .eq('assignment_status', 'pending_owner'),
    supabase
      .from('location_logs')
      .select('vehicle_id, geopoint, timestamp, trip_id, driver_id')
      .in('vehicle_id', vehicleIds)
      .gte('timestamp', logsSince)
      .order('timestamp', { ascending: false })
      .limit(3000),
  ]);

  const onTripByVehicle = new Map<string, { tripId: string; driverId: string | null }>();
  for (const trip of activeTrips ?? []) {
    if (!trip.vehicle_id || onTripByVehicle.has(trip.vehicle_id)) continue;
    onTripByVehicle.set(trip.vehicle_id, {
      tripId: trip.id,
      driverId: trip.driver_id ?? null,
    });
  }

  const pendingVehicleIds = new Set(
    (pendingTrips ?? []).map((t) => t.vehicle_id).filter(Boolean) as string[]
  );

  const latestLogByVehicle = new Map<
    string,
    { lat: number; lng: number; timestamp: string; tripId: string | null; driverId: string | null }
  >();
  for (const log of logs ?? []) {
    if (!log.vehicle_id || latestLogByVehicle.has(log.vehicle_id)) continue;
    const point = log.geopoint ? parseGeoPoint(log.geopoint) : null;
    if (!point) continue;
    latestLogByVehicle.set(log.vehicle_id, {
      lat: point.lat,
      lng: point.lng,
      timestamp: log.timestamp,
      tripId: log.trip_id ?? null,
      driverId: log.driver_id ?? null,
    });
  }

  return vehicles.map((v) => {
    const log = latestLogByVehicle.get(v.id);
    const onTrip = onTripByVehicle.get(v.id);
    const lastSeenAt = log?.timestamp ?? null;
    const lastMs = lastSeenAt ? new Date(lastSeenAt).getTime() : NaN;
    const online = Number.isFinite(lastMs) && lastMs >= onlineCutoff;
    const hasPoint = log != null;

    let displayStatus: FleetMapDisplayStatus = 'unknown';
    if (v.status === 'maintenance') {
      displayStatus = 'maintenance';
    } else if (onTrip) {
      displayStatus = 'on_trip';
    } else if (pendingVehicleIds.has(v.id)) {
      displayStatus = 'pending';
    } else if (hasPoint && Number.isFinite(lastMs) && lastMs < staleCutoff) {
      displayStatus = 'stale';
    } else if (hasPoint && v.status === 'active') {
      displayStatus = 'available';
    } else if (!hasPoint) {
      displayStatus = 'unknown';
    } else {
      displayStatus = 'available';
    }

    return {
      vehicleId: v.id,
      fleetNumber: v.fleet_number,
      registrationPlate: v.registration_plate ?? null,
      vehicleStatus: v.status,
      fleetOperatorId: v.fleet_operator_id ?? null,
      lat: log?.lat ?? null,
      lng: log?.lng ?? null,
      lastSeenAt,
      tripId: onTrip?.tripId ?? log?.tripId ?? null,
      driverId: onTrip?.driverId ?? log?.driverId ?? null,
      displayStatus,
      online,
    };
  });
}

export function countOnlineVehicles(rows: FleetVehiclePosition[]): number {
  return rows.filter((r) => r.online).length;
}

export function fleetPositionsToMapMarkers(
  rows: FleetVehiclePosition[]
): { id: string; position: { lat: number; lng: number }; title: string; subtitle?: string; role: 'vehicle' }[] {
  return rows
    .filter((r) => r.lat != null && r.lng != null)
    .map((r) => ({
      id: r.vehicleId,
      position: { lat: r.lat as number, lng: r.lng as number },
      title: `${r.fleetNumber}${r.registrationPlate ? ` · ${r.registrationPlate}` : ''}`,
      subtitle: `${r.displayStatus}${r.lastSeenAt ? ` · ${r.lastSeenAt}` : ''}`,
      role: 'vehicle' as const,
    }));
}
